import { Component, EventEmitter, Inject, Injector, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import { cloneDeep } from 'lodash';
import { MbsPopupType, MbsSize, ModalService, WizardStepComponent } from 'mbs-ui-kit';
import { PermissionsEnum, RoutingPath } from 'mbs-ui-shared';
import { AuthFacade } from 'mbs-ui-shared/src/lib/facades/auth.facade';
import { ComputerTagsFacade } from 'mbs-ui-shared/src/lib/facades/computer.tags.facade';
import { ComputersFacade } from 'mbs-ui-shared/src/lib/facades/computers.facade';
import Computer from 'mbs-ui-shared/src/lib/models/Computer';
import { Tag } from 'mbs-ui-shared/src/lib/models/tag';
import { UtilitySelectionModalComponent } from 'mbs-ui-shared/src/lib/modules/utility-selection-modal/utility-selection-modal.component';
import { ComputerFromRegGroupTask } from 'mbs-ui-shared/src/lib/store/rmmGroupTasks/model';
import {
  NotificationProfileTypesInNumber,
  RmmNotificationProfile,
  RmmProfileApplyTo
} from 'mbs-ui-shared@models/rmm-custom-notification-model';
import { RmmNotificationService } from 'mbs-ui-shared@services/rmm-notifications.service';
import moment from 'moment';
import { BehaviorSubject, from, noop } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, take } from 'rxjs/operators';
import { LoadRmmCustomNotificationWizardService } from '../../utility/load-rmm-custom-notification-wizard.service';
import { rmmIntersectionHeaders, tagHeaders } from './apply-profile-to.constants';

export enum SelectOptions {
  ApplyToAllCompanies,
  ApplyToAllComputersInSelectedCompanies,
  ApplyToSelectedComputersOnly
}
@UntilDestroy()
@Component({
  selector: 'mbs-rmm-notifications-apply-profile-to',
  templateUrl: './apply-profile-to.component.html',
  host: {
    class: 'rmm-group-action'
  }
})
export class RmmNotificationsApplyProfileToComponent implements OnInit {
  @ViewChild('tagSelectionModal', { static: true, read: TemplateRef }) tagSelectionModal: TemplateRef<any>;
  @ViewChild('intesectionModalContent', { static: false }) public intesectionModalContent: TemplateRef<any>;
  @Output() disableNext = new EventEmitter<boolean>();

  public readonly alertType = MbsPopupType;

  public selectedComputerList: Computer[] = [];
  public selectedExcludedComputersList: Computer[] = [];
  public selectedCompanyList: { id: string; name: string }[] = [];
  public excludedComputersEnabled = false;
  public selectOptions = SelectOptions;
  public modeSelectionForm: UntypedFormGroup = new UntypedFormGroup({
    modeSelect: new FormControl(),
    enableExclude: new FormControl(false),
    enableTags: new FormControl(false),
    tags: new FormControl([])
  });

  public canUseAllCompanies = true;
  public selectTagEnabled = false;
  public selectedTagList: string[] = [];
  public tags: Tag[] = [];
  public selectedTagNames: Array<{ id: string; name: string; removed?: boolean }> = [];
  public disableChanges = false;
  public intersectionData: Partial<RmmNotificationProfile & { priority?: boolean }>[] = [];
  public intersectionError = false;
  public intersectionHeaders = rmmIntersectionHeaders(this.i18n);

  public tagHeaders = tagHeaders;
  public loading = true;
  public disableTagAndExclude = false;

  public profileSettings = new UntypedFormGroup({
    name: new FormControl()
  });

  public modeItems = [
    {
      id: this.selectOptions.ApplyToAllCompanies,
      value: this.i18n.transform('mbs-ui:rmm-custom-alerting.wizard.firstStep.applyAll')
    },
    {
      id: this.selectOptions.ApplyToAllComputersInSelectedCompanies,
      value: this.i18n.transform('mbs-ui:rmm-custom-alerting.wizard.firstStep.applyCompany')
    },
    {
      id: this.selectOptions.ApplyToSelectedComputersOnly,
      value: this.i18n.transform('mbs-ui:rmm-custom-alerting.wizard.firstStep.applyComputer')
    }
  ];
  private readonly defaultProfileName = 'New Profile';
  private tzOffset: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  private computers: Computer[];

  constructor(
    protected injector: Injector,
    @Inject(WizardStepComponent) protected parent: WizardStepComponent,
    private modalService: ModalService,
    private i18n: I18NextPipe,
    private loadRmmCustomNotificationWizardService: LoadRmmCustomNotificationWizardService,
    private tagsFacade: ComputerTagsFacade,
    private computerFacade: ComputersFacade,
    private rmmNotificationService: RmmNotificationService,
    private auth: AuthFacade,
    private ability: AbilityService
  ) {
    this.disableChanges = !this.loadRmmCustomNotificationWizardService.canEditApplyToStep;

    this.tagsFacade
      .loadAllTags()
      .pipe(untilDestroyed(this))
      .subscribe((tags) => {
        this.tags = tags;
        if (this.loadRmmCustomNotificationWizardService.editMode || this.loadRmmCustomNotificationWizardService.applyTo.getValue())
          this.loadApplyToData();

        this.setupProfile();
      });

    this.modeSelectionForm
      .get('tags')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((tagIDs) => {
        const excludedTags = tagIDs
          .filter((id) => !this.tags.some((tag) => `${tag.id}` === id))
          .map((excludedID) => ({
            id: excludedID,
            removed: true,
            name: this.i18n.transform('shared:rmm-group-task.removedTagName', { value: excludedID })
          }));

        this.selectedTagNames = this.tags
          .filter((tag) => tagIDs.includes(`${tag.id}`))
          .map((tag) => ({ id: `${tag.id}`, name: tag.name }))
          .concat(excludedTags);

        if (this.modeSelectionForm.get('enableTags').value) this.onComputerModeChange(this.modeSelectionForm.get('modeSelect').value);
      });
  }

  setupProfile() {
    this.profileSettings
      .get('name')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((name) => {
        this.loadRmmCustomNotificationWizardService.profileName.next(name);
        if (!name?.trim()) {
          this.disableNext.emit(true);
        } else {
          this.onComputerModeChange(this.modeSelectionForm.get('modeSelect').value, false);
        }
      });
  }

  loadApplyToData() {
    this.disableTagAndExclude = this.loadRmmCustomNotificationWizardService.profileType === NotificationProfileTypesInNumber.Global;
    const selectedEntities = cloneDeep(this.loadRmmCustomNotificationWizardService.applyTo.getValue());

    this.selectedComputerList = selectedEntities.computers.filter((computer) => !computer.disabled);
    this.selectedExcludedComputersList = selectedEntities.computers.filter((computer) => computer.disabled);
    if (this.selectedExcludedComputersList.length) this.excludedComputersEnabled = true;

    this.selectedCompanyList = selectedEntities.companies.map((company) => ({ id: company.companyGuid, name: company.name }));
    this.modeSelectionForm.get('enableExclude').setValue(!!this.selectedExcludedComputersList.length, { emitEvent: false });

    this.setupTagsOnLoad(selectedEntities);
    this.setupModeSelectionOnLoad(selectedEntities);

    this.loading = false;
    this.onComputerModeChange(this.modeSelectionForm.get('modeSelect').value);
  }

  private setupModeSelectionOnLoad(selectedEntities: RmmProfileApplyTo) {
    let modeSelect = null;

    if (selectedEntities.allCompanies) modeSelect = SelectOptions.ApplyToAllCompanies;
    if (selectedEntities.companies.length) modeSelect = SelectOptions.ApplyToAllComputersInSelectedCompanies;
    if (selectedEntities.computers.some((computer) => computer.disabled === false)) modeSelect = SelectOptions.ApplyToSelectedComputersOnly;

    this.modeSelectionForm.get('modeSelect').setValue(modeSelect, { emitEvent: false });
  }

  private setupTagsOnLoad(selectedEntities: RmmProfileApplyTo) {
    this.selectedTagList = selectedEntities.computerTags.map((tag) => tag.toString());
    this.selectTagEnabled = !!this.selectedTagList.length;

    this.selectedTagNames = this.tags
      .filter((tag) => selectedEntities.computerTags.includes(tag.id))
      .map((tag) => ({ id: `${tag.id}`, name: tag.name }));

    this.modeSelectionForm.patchValue({
      tags: [...this.selectedTagList],
      enableTags: this.selectTagEnabled
    });
  }

  ngOnInit(): void {
    this.auth.currentUser$.pipe(filter(Boolean), take(1)).subscribe((user) => {
      if (user.ProviderInfo.TimeZoneSpecified) this.tzOffset.next(user.ProviderInfo.TimeZoneOffset);

      this.profileSettings
        .get('name')
        .setValue(
          this.loadRmmCustomNotificationWizardService.profileName.getValue() ??
            `${this.defaultProfileName} ${moment(new Date()).utcOffset(this.tzOffset.getValue()).format('MMM DD, YYYY, h:mm A')}`
        );
    });

    this.modeSelectionForm
      .get('modeSelect')
      .valueChanges.pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((value) => {
        this.intersectionData = [];
        this.onComputerModeChange(value);
      });

    this.modeSelectionForm
      .get('enableExclude')
      .valueChanges.pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((value) => {
        this.excludedComputersEnabled = value;
        if (value && !this.selectedExcludedComputersList.length) this.openSelectComputersModal(true);
        this.onComputerModeChange(this.modeSelectionForm.get('modeSelect').value);
      });

    this.modeSelectionForm
      .get('enableTags')
      .valueChanges.pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((value) => {
        this.selectTagEnabled = value;
        if (value && !this.selectedTagList.length) this.openSelectTagsModal();

        this.onComputerModeChange(this.modeSelectionForm.get('modeSelect').value);
      });

    this.canUseAllCompanies = this.ability.can('read', PermissionsEnum.AccessToAllCompanies);
    if (!this.canUseAllCompanies && this.modeSelectionForm.get('modeSelect').value === this.selectOptions.ApplyToAllCompanies)
      this.modeSelectionForm.get('modeSelect').setValue(this.selectOptions.ApplyToAllComputersInSelectedCompanies);
  }

  trackByFn(index: number, item: Computer): string {
    return item.hid;
  }

  /* eslint-disable no-case-declarations */
  public onComputerModeChange(formValue: number, shouldCheckIntersection = true): void {
    this.intersectionError = false;
    this.intersectionData = [];

    const nameIsValid = !!this.profileSettings.get('name').value?.trim();

    switch (formValue) {
      case this.selectOptions.ApplyToAllComputersInSelectedCompanies:
        this.loadRmmCustomNotificationWizardService.applyTo.next({
          companies: this.selectedCompanyList.map((company) => ({ companyGuid: company.id, name: company.name, disabled: false })),
          computers: this.excludedComputersEnabled ? this.getPickedComputerList() : [],
          allCompanies: false,
          computerTags: this.selectTagEnabled ? this.selectedTagList.map((id) => Number(id)) : []
        });
        this.disableNext.emit(!this.selectedCompanyList.length || !nameIsValid);
        if (shouldCheckIntersection && this.selectedCompanyList.length) this.intersectionCheck();
        if (!this.selectedCompanyList.length) this.intersectionData = [];
        break;
      case this.selectOptions.ApplyToSelectedComputersOnly:
        const computers = this.excludedComputersEnabled
          ? this.getPickedComputerList()
          : this.selectedComputerList.map((computer: any) => ({ hid: computer.hid, name: computer.name, disabled: false }));

        this.loadRmmCustomNotificationWizardService.applyTo.next({
          companies: [],
          computers,
          allCompanies: false,
          computerTags: this.selectTagEnabled ? this.selectedTagList.map((id) => Number(id)) : []
        });

        this.disableNext.emit(!computers.some((computer) => !computer.disabled) || !nameIsValid);
        if (shouldCheckIntersection && computers.some((computer) => !computer.disabled)) this.intersectionCheck();
        if (!this.selectedComputerList.length) this.intersectionData = [];
        break;
      case this.selectOptions.ApplyToAllCompanies:
        this.loadRmmCustomNotificationWizardService.applyTo.next({
          companies: [],
          computers: this.excludedComputersEnabled ? this.getPickedComputerList() : [],
          allCompanies: true,
          computerTags: this.selectTagEnabled ? this.selectedTagList.map((id) => Number(id)) : []
        });

        const withExclude = this.excludedComputersEnabled && this.selectedExcludedComputersList.length;
        const withTags = this.selectTagEnabled && this.selectedTagList.length;

        if (
          !withExclude &&
          !withTags &&
          this.loadRmmCustomNotificationWizardService?.profileType !== NotificationProfileTypesInNumber.Global
        ) {
          this.intersectionError = true;
          this.intersectionData = [];
          this.disableNext.emit(true);
        } else {
          this.disableNext.emit(!nameIsValid);
          this.intersectionCheck();
        }
        break;
      default:
        this.disableNext.emit(true);
        break;
    }
  }

  private getPickedComputerList(): ComputerFromRegGroupTask[] {
    return [
      ...this.selectedComputerList
        .filter((computer) => !this.selectedExcludedComputersList.some((excluded) => excluded.hid === computer.hid))
        .map((computer) => ({ hid: computer.hid, name: computer.name, disabled: false }))
    ].concat(this.selectedExcludedComputersList.map((computer) => ({ hid: computer.hid, name: computer.name, disabled: true })));
  }

  openSelectComputersModal(excludeMode = false): void {
    if (excludeMode && !this.modeSelectionForm.get('enableExclude').value && this.tags) {
      this.modeSelectionForm.get('enableExclude').setValue(true);

      if (!this.selectedExcludedComputersList.length) return;
    }

    !excludeMode && this.modeSelectionForm.get('modeSelect').setValue(this.selectOptions.ApplyToSelectedComputersOnly);

    const groups = excludeMode ? this.selectedExcludedComputersList.map((x) => x.hid) : this.selectedComputerList.map((x) => x.hid);
    this.modalService
      .openCustom(UtilitySelectionModalComponent, {
        data: {
          model: groups,
          computers: this.computers,
          modalTitle: excludeMode
            ? this.i18n.transform('shared:rmm.groupActions.stepApplyTo.selectionModal.selectExcludedComputersModalTitle')
            : this.i18n.transform('shared:rmm.groupActions.stepApplyTo.selectionModal.selectComputersModalTitle'),
          applyTo: 'Computer List',
          noComputersText: this.i18n.transform('shared:rmm.groupActions.stepApplyTo.selectionModal.selectComputersModalNoComputersDesc'),
          saveButtonText: excludeMode ? this.i18n.transform('shared:buttons.confirm') : this.i18n.transform('shared:buttons.apply'),
          getComputersFromStore: this.computerFacade.getData$
        },
        size: MbsSize.sm,
        collapsing: true
      })
      .then((selectedEntities: any) => {
        if (selectedEntities) {
          excludeMode
            ? (this.selectedExcludedComputersList = selectedEntities.flatMap((entity) => entity.selectedItems.map((computer) => computer)))
            : (this.selectedComputerList = selectedEntities.flatMap((entity) => entity.selectedItems.map((computer) => computer)));
        }
        this.onComputerModeChange(this.modeSelectionForm.get('modeSelect').value);
      })
      .catch(noop);
  }

  openSelectCompaniesModal(): void {
    this.modeSelectionForm.get('modeSelect').setValue(this.selectOptions.ApplyToAllComputersInSelectedCompanies);
    const groups = this.selectedCompanyList.map((companyId) => companyId.id);
    this.modalService
      .openCustom(UtilitySelectionModalComponent, {
        data: {
          model: groups,
          computers: this.computers,
          modalTitle: this.i18n.transform('shared:rmm.groupActions.stepApplyTo.selectionModal.selectCompaniesModalTitle'),
          applyTo: 'Company List',
          noComputersText: this.i18n.transform('shared:rmm.groupActions.stepApplyTo.selectionModal.selectCompaniesModalNoCompaniesDesc'),
          saveButtonText: this.i18n.transform('shared:buttons.apply')
        },
        size: MbsSize.sm,
        collapsing: true
      })
      .then((selectedEntities: any) => {
        if (selectedEntities) {
          this.selectedCompanyList = selectedEntities.map((entity) => ({
            id: entity.id,
            name: entity.title
          }));
        }
        this.onComputerModeChange(this.modeSelectionForm.get('modeSelect').value);
      })
      .catch(noop);
  }

  openSelectTagsModal(): void {
    if (!this.modeSelectionForm.get('enableTags').value) {
      this.modeSelectionForm.get('enableTags').setValue(true);

      if (!this.selectedTagList.length) return;
    }

    this.modalService
      .open(
        {
          header: { title: this.i18n.transform('shared:rmm.groupActions.stepApplyTo.selectTags', { format: 'title' }) },
          size: MbsSize.sm,
          footer: {
            show: true,
            cancelButton: { text: this.i18n.transform('shared:buttons.cancel') },
            okButton: { text: this.i18n.transform('shared:buttons.save') }
          },
          collapsing: true
        },
        this.tagSelectionModal
      )
      .then((confirmed) => {
        if (confirmed) {
          this.modeSelectionForm.get('tags').setValue([...this.selectedTagList]);
        } else {
          this.selectedTagList = [...this.modeSelectionForm.get('tags').value];
        }
      })
      .catch(noop);
  }

  changeTagsHandler(data: string[]): void {
    this.selectedTagList = data.map((data) => data);
  }

  handleClose(tag: { id: string; name: string }) {
    this.selectedTagList = this.selectedTagList.filter((id) => id !== tag.id);
    this.modeSelectionForm.get('tags').setValue([...this.selectedTagList]);
  }

  public intersectionCheck() {
    if (this.loading) return;

    this.loadRmmCustomNotificationWizardService.applyTo
      .pipe(
        debounceTime(300),
        switchMap((value) =>
          this.rmmNotificationService.checkIntersectionData(
            this.loadRmmCustomNotificationWizardService.applyTo.getValue(),
            this.loadRmmCustomNotificationWizardService.profileId
              ? { profileId: this.loadRmmCustomNotificationWizardService.profileId }
              : {}
          )
        ),
        take(1)
      )
      .subscribe({
        next: (response) => {
          if (
            response.profiles.length &&
            this.modeSelectionForm.get('modeSelect').value === this.selectOptions.ApplyToSelectedComputersOnly
          ) {
            this.intersectionError = true;
            this.intersectionData = [];
            this.disableNext.emit(true);
          } else {
            this.intersectionError = false;

            this.intersectionData = response.profiles?.map((item) => {
              return {
                ...item,
                priority:
                  item.profileType === NotificationProfileTypesInNumber.Computer
                    ? true
                    : moment(this.loadRmmCustomNotificationWizardService.created).isBefore(item.created)
              };
            });
          }
        },
        error: () => {
          this.intersectionError = true;
          this.intersectionData = [];
          this.disableNext.emit(true);
        }
      });
  }

  openIntersectionModalStream() {
    from(
      this.modalService.open(
        {
          header: {
            title: this.i18n.transform('mbs-ui:rmm-custom-alerting.intersectionModalTitle')
          },
          size: MbsSize.md,
          footer: { show: false }
        },
        this.intesectionModalContent
      )
    )
      .pipe(untilDestroyed(this))
      .subscribe({ next: noop, error: noop });
  }

  openGlobalProfileOnSeparatePage() {
    return this.rmmNotificationService
      .getProfileList({ profileType: 'global' })
      .pipe(take(1))
      .subscribe((value) => {
        window.open(`${window.location.origin}/${RoutingPath.RMMNotifications}?profileId=${value.data[0].id}`, '_blank');
      });
  }
}
